// Mic / dictate button rendered next to the BugReportButton textarea.
// Gated by the caller (platform_owner / platform_dev). Tapping toggles
// Deepgram streaming and appends incoming transcript chunks to the
// description via the supplied onAppend callback.
//
// Plan: dev sync note 816024b6.

import { useBugReportMic } from "./useBugReportMic";

type Props = {
  onAppend: (chunk: string) => void;
  disabled?: boolean;
};

export function BugReportMicButton({ onAppend, disabled }: Props) {
  const { status, error, start, stop } = useBugReportMic(onAppend);
  const listening = status === "listening";
  const busy = status === "starting" || status === "stopping";

  const label = listening ? "Stop" : busy ? "…" : "Dictate";
  const ariaLabel = listening
    ? "Stop dictation"
    : busy
      ? "Mic warming up"
      : "Start voice dictation";

  const handleClick = () => {
    if (listening) {
      void stop();
    } else if (!busy) {
      void start();
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={Boolean(disabled) || busy}
        aria-pressed={listening}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={
          "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-60 " +
          (listening
            ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
            : "border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100")
        }
      >
        <span
          aria-hidden
          className={
            "inline-block h-1.5 w-1.5 rounded-full " +
            (listening ? "bg-red-600 animate-pulse" : "bg-violet-500")
          }
        />
        <span>{label}</span>
      </button>
      {error ? (
        <span className="max-w-[12rem] text-right text-[10px] text-red-700">{error}</span>
      ) : null}
    </div>
  );
}
